import { SetStateAction } from "react";
import { TReportField, TReportWithFields } from "../../../api/types/Report";
import { ListingResponse } from "../../../api/types/Listing";
import { CustomWidget, CustomWidgetBasic } from "../../../api/types/Overview";
import { useLazyRunCustomReportQuery } from "../../../api/network/reportApiServices";
import { REPORT_FILTER_TYPES } from "../../reports/constants";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { handleAuthErrorCode } from "../../../api/network/errorCodes";
import { APP_CONFIG } from "../../../constants/constants";
import useConversion from "../../../hooks/conversion";
import { REPORT_FIELD_OUTPUT_TYPES } from "../../reports/constants";

export const useChartUtils = () => {
  const [runCustomReportAPITrigger] = useLazyRunCustomReportQuery();
  const { convertToUnit } = useConversion();
  const checkAndFetchDrillDownData = ({
    retrievedComputedChartData,
    dataSpecificReport,
    dataReport,
    isFetching,
    setChartGroupingLabels,
    setComputedChartData,
    setFetchingWidgetData,
    reportId,
    widget,
    graphXAxisDataKey,
    graphYAxisDataKey,
    fetchCompleteCallback,
  }: {
    retrievedComputedChartData: any[];
    dataSpecificReport: TReportWithFields | undefined;
    dataReport: ListingResponse<any[]> | undefined;
    isFetching: boolean;
    setChartGroupingLabels: (value: SetStateAction<string[]>) => void;
    setComputedChartData: (value: SetStateAction<any[]>) => void;
    setFetchingWidgetData: (value: SetStateAction<boolean>) => void;
    reportId: number | undefined;
    widget: CustomWidgetBasic | undefined;
    graphXAxisDataKey: string;
    graphYAxisDataKey: string;
    fetchCompleteCallback?: () => void;
  }) => {
    if (!!dataSpecificReport && !!dataReport && !isFetching) {
      // check if sub-grouping is enabled
      const groupByField = dataSpecificReport.report_fields.find(
        (field) => field.identifier === widget?.x_axis
      );
      const subGroupByField = dataSpecificReport.report_fields.find(
        (field) => field.identifier === widget?.group_by
      );
      if (!!widget?.group_by && !!groupByField && !!subGroupByField) {
        // sub-grouping is enabled
        const rowDrillDownPromises = dataReport.results.map((row: any) => {
          if (!reportId?.toString() || reportId?.toString() === "") {
            if (APP_CONFIG.DEBUG.ALL)
              console.log("reportId?.toString() IS BLANK"); // alert
          }
          return runCustomReportAPITrigger({
            id: reportId?.toString() ?? "",
            fields: dataSpecificReport?.report_fields
              ?.map((field) => {
                return field.id;
              })
              .join(","),
            page: 1,
            page_size: 10,
            report_fields: dataSpecificReport?.report_fields?.map(
              (field: TReportField) => {
                const groupByIsActive = field.identifier === widget?.x_axis;
                return {
                  field: parseInt(field.field.all_field_id ?? "0"),
                  // aggregation param
                  ...(!!field.aggregate_function
                    ? {
                        aggregate_function: field.aggregate_function,
                        column_type: field.column_type,
                        value: field.value,
                        operator: field.operator,
                      }
                    : {}),
                  // group by param
                  ...(groupByIsActive
                    ? {
                        is_group_by: field.identifier === widget.x_axis,
                        group_order: field.group_order,
                      }
                    : {}),
                  ...(field.identifier === widget.group_by
                    ? {
                        is_group_by: field.identifier === widget.group_by,
                        group_order: field.group_order,
                        value: row[graphXAxisDataKey],
                      }
                    : {}),
                };
              }
            ),
            report_filters: [
              ...dataSpecificReport?.report_filters,
              {
                filter_type: REPORT_FILTER_TYPES.COLUMN,
                operator: "=",
                param: widget.x_axis ?? "",
                value: row[graphXAxisDataKey],
              },
            ],
            deviceid: "",
          });
        });
        // when all promises are resolved
        Promise.all(rowDrillDownPromises).then((promiseResults) => {
          // if any of the promises fail with a 401, then we redirect to login
          const anyPromiseFailed = promiseResults.find(
            (promiseResult) =>
              !promiseResult.isSuccess &&
              (promiseResult?.error as FetchBaseQueryError)?.status ===
                RESPONSE_CODE.UNAUTHORIZED
          );
          if (!!anyPromiseFailed) {
            handleAuthErrorCode(anyPromiseFailed?.error as FetchBaseQueryError);
            return;
          }
         const fieldData = dataSpecificReport?.report_fields?.find(
            (field) => field.identifier === graphYAxisDataKey
          );

          // store the drill-down data against each row in computedChartData
          const updatedComputedChartData = [...retrievedComputedChartData]; // [...computedChartData];
          let groupingLabels: string[] = [];
          promiseResults
            .filter((promiseResult) => !!promiseResult.isSuccess)
            .forEach((promiseResult, index) => {
              const resultForGroupingFilter =
                promiseResult?.originalArgs?.report_filters?.[
                  promiseResult?.originalArgs?.report_filters?.length - 1
                ];
              const resultForValue = resultForGroupingFilter?.value;
              // console.log("resultForGroupingFilter", resultForGroupingFilter);
              // console.log("resultForParam", resultForParam);
              // console.log("resultForValue", resultForValue);
              // console.log("result", promiseResult?.data?.results?.length);
              // update the computedChartData with the drill-down data
              const indexToUpdate = updatedComputedChartData?.findIndex(
                (item) => {
                  return item[graphXAxisDataKey] === resultForValue;
                }
              );
              if (indexToUpdate > -1) {
                updatedComputedChartData[indexToUpdate] = {
                  ...updatedComputedChartData[indexToUpdate],
                  ...promiseResult?.data?.results?.reduce((acc, item) => {
                    // store the grouping labels
                    if (!groupingLabels.includes(item[widget.group_by])) {
                      groupingLabels.push(item[widget.group_by]);
                    }
                    // store the grouping data
                    return {
                    ...acc,
                    [item[widget.group_by]]: parseFloat(
                      convertToUnit(
                        fieldData?.output_type || REPORT_FIELD_OUTPUT_TYPES.TEXT,
                        item[graphYAxisDataKey],
                        fieldData?.convert_to_whole_number
                      )?.finalData || "0"
                    ),
                  };
                }, {}),
                };
              }
            });
          // console.log("updatedComputedChartData", updatedComputedChartData);
          setComputedChartData(updatedComputedChartData);
          setChartGroupingLabels(groupingLabels);

          // set fetching to false
          setTimeout(() => {
            setFetchingWidgetData(false);
            fetchCompleteCallback?.();
          }, 1000);
        });
      } else {
        // sub-grouping is disabled
        setFetchingWidgetData(false);
        fetchCompleteCallback?.();
      }
    }
  };

  return {
    checkAndFetchDrillDownData,
  };
};
